import { useMap } from 'react-leaflet';
import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import { useTranslation } from 'react-i18next';

type GeocoderControlProps = {
  onFound?: (coords: { lat: number; lng: number }, address: Record<string, string>) => void;
};

export const GeocoderControl = ({ onFound }: GeocoderControlProps) => {
  const map = useMap();
  const containerRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [isFocused, setIsFocused] = useState(false);

  const { t } = useTranslation('commons')
  /**
   * Uses Nominatim API to search for addresses.
   * @param query
   * @returns
   */
  const searchAddress = async (query: string) => {
    const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(
      query
    )}&format=json&limit=5&addressdetails=1`;
    const res = await fetch(url);
    return await res.json();
  };

  /**
   * Handles the selection of a place from the suggestions.
   * @param place
   */
  const handleSelect = (place: any) => {
    // TODO: replace with a proper type, use only coordinates and display_name ?
    const lat = parseFloat(place.lat);
    const lng = parseFloat(place.lon);
    const center = { lat, lng };
    map.setView(center, 16);

    setQuery(place.display_name);
    setSuggestions([]);
    setIsFocused(false);

    if (onFound) {
      onFound(center, place.address);
    }
  };

  /**
   * HAndle the input key down event.
   * @param e
   */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const selectedPlace = suggestions[0];
      if (selectedPlace) {
        handleSelect(selectedPlace);
      } else {
        //if no suggesions
        searchAddress(query).then((results) => {
          if (results.length > 0) {
            handleSelect(results[0]);
          }
        });
      }
    }
  };

  // Debounced search
  useEffect(() => {
    if (!query || query.length < 3) {
      setSuggestions([]);
      return;
    }
    const timeout = setTimeout(async () => {
      const results = await searchAddress(query);
      setSuggestions(results);
    }, 300);

    return () => clearTimeout(timeout);
  }, [query]);

  // Close suggestions on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setSuggestions([]);
        setIsFocused(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (containerRef.current) {
      L.DomEvent.disableClickPropagation(containerRef.current); // stop clcick propagation on the map
    }
  }, []);

  return (
    <div ref={containerRef} className="geocoder-control">
      <div className="uk-position-relative address-search">
        <span className="uk-form-icon" uk-icon="icon: search"></span>
        <input
          className="uk-input"
          type="text"
          value={query}
          onKeyDown={handleKeyDown}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setIsFocused(true)}
          placeholder={t("Rechercher une adresse...")}
        />
      </div>
      {isFocused && suggestions.length > 0 && (
        <div className="suggestions-list">
          {suggestions.map((place, idx) => (
            <div
              key={idx}
              onClick={() => handleSelect(place)}
              style={{
                padding: '10px 16px',
                cursor: 'pointer',
              }}
              onMouseOver={(e) =>
                (e.currentTarget.style.backgroundColor = '#f0f0f0')
              }
              onMouseOut={(e) =>
                (e.currentTarget.style.backgroundColor = 'white')
              }
            >
              {place.display_name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
